import React, { useCallback, useMemo } from 'react'

import { I18n, CDNSVG, hooks, CDNImage } from '@front/volcanion'
import GeoUtils from '@front/volcanion/utils/geo'
import { TimeUtils } from '@front/volcanion/utils'

import { Box, Divider, Typography as MUITypography, Chip, Icon, Link, IconButton, Button } from '@mui/material'

import moment from 'moment'

const Typography = (props) => <MUITypography {...props} m='unset !important' fontSize={16} fontWeight={500} />

const RowItem = ({ value, icon, href, children }) =>
  <Box display='flex' alignItems='center' justifyContent='space-between'>
    <Box display='flex' alignItems='center' gap={1}>
      <Icon color='primary'>{icon}</Icon>
      {!href &&
        <Typography>{value}</Typography>
      }
      {!!href &&
        <Link color='white' target='_blank' href={href}>
          <Typography color='primary'>{value}</Typography>
        </Link>
      }
    </Box>
    {children}
  </Box>

const DriverSection = ({ vehicle, onCall }) => {
  const status = vehicle?.status
  const chipColor = useMemo(() => {
    switch (status) {
      case 'available':
        return 'green'
      case 'unavailable':
        return 'red'
      case 'in_use':
        return 'orange'
      case 'en_route':
        return 'blue'
      default:
        return
    }
  }, [status])
  const handleOnCall = useCallback(() => onCall(vehicle?.driver?.auth?.gsm), [vehicle?.driver?.auth?.gsm])

  return (
    <Box display='flex' flexDirection='column' gap={1}>
      <Box display='flex' justifyContent='space-around' alignItems='center' gap={2}>
        <Typography>{I18n.t('supervision.vehicle.card.driver_info')}</Typography>
        <Chip size='small' label={I18n.t(`sqr.vehicle.status.${status}`)} sx={{ bgcolor: chipColor, color: 'white' }} />
      </Box>
      <Divider />
      <RowItem
        icon='person'
        value={`${vehicle?.driver?.info?.first_name} ${vehicle?.driver?.info?.last_name} - ${vehicle?.driver?.info?.user_client_id}`}
        href={`/driver/${vehicle?.driver?.user_id}`}
      />
      <RowItem
        icon='phone'
        value={vehicle?.driver?.auth?.gsm}
      >
        <IconButton onClick={handleOnCall} sx={{ m: "1px", p: 0 }}>
          <CDNSVG sx={{ fontSize: 30, color: 'icon.main' }} name='customer-call' />
        </IconButton>
      </RowItem>
      <RowItem
        icon='tune'
        value={_.map(vehicle?.driver?.options, 'name_translated').join(', ')}
      />
    </Box>
  )
}

const VehicleSection = ({ vehicle }) => {
  return (
    <Box display='flex' flexDirection='column' gap={1}>
      <Divider />
      <Typography fontSize={16}>{I18n.t('supervision.vehicle.card.vehicle_info')}</Typography>
      <Divider />
      <Box display='flex' flexDirection='row' gap={1}>
        <CDNSVG name={vehicle?.type?.icon} variant={'map'} />
        <Typography>{vehicle?.type?.name_translated}</Typography>
        <Typography color='primary'>
          {_.compact([
            vehicle?.info?.model?.brand?.name,
            vehicle?.info?.model?.name,
            _.toLower(vehicle?.info?.color)
          ]).join(' ')}
        </Typography>
        <Typography color='primary'>-</Typography>
        <Link color='white' target='_blank' href={`/adherent/${vehicle?.group}/vehicle/${vehicle?.vehicle_id}`}>
          <Typography color='primary'>{vehicle?.info?.plaque}</Typography>
        </Link>
      </Box>
      <RowItem icon='tune' value={_.map(vehicle?.options, 'name_translated').join(', ')} />
    </Box>
  )
}

const OrderSection = ({ order }) => {
  const status = hooks.useOrderStatus(order, { translated: true })
  const statusColor = hooks.useOrderStatusColor(order)

  const is_comming = _.includes(['planned', 'started'], order?.assigned_transport?.status)
  const live_eta = order?.current_target?.eta?.time
  const { src_datetime, dest_datetime } = order?.info || {}

  const src_eta = TimeUtils.secondsToTime(
    !!live_eta
      ? moment(live_eta).diff(moment(), 'seconds')
      : moment(src_datetime).diff(moment(order?.requestedAt), 'seconds'), null, { hide_seconds: true }
  )
  const dest_eta = !!dest_datetime && TimeUtils.secondsToTime(moment(dest_datetime).diff(moment(src_datetime), 'seconds'), null, { hide_seconds: true })
  const eta = is_comming ? src_eta : dest_eta

  return (
    <Box display='flex' flexDirection='column' gap={1}>
      <Divider />
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Link target='_blank' underline="always" href={`/order/${order?.order_id}`}>
          <Typography color='primary'>{order?.order_client_id}</Typography>
        </Link>
        <Chip size='small' label={status} sx={{ bgcolor: statusColor, color: 'white' }} />
      </Box>
      <Divider />
      <Box display='flex' alignItems='center' gap={1}>
        <CDNImage name={is_comming ? 'source' : 'destination'} category='map' style={{ width: 20, height: 20 }} />
        <Typography>{GeoUtils.format(is_comming ? order?.source : order?.destination)}</Typography>
      </Box>
      <RowItem
        icon={!!live_eta ? 'bolt' : 'watch_later'}
        value={
          _.includes(['at_src', 'off_board'], order?.assigned_transport?.status) || !eta ? '-' : eta
        }
      />
    </Box>
  )
}

const VehicleCard = ({
  isCurrent,
  vehicle,
  order,
  onCall,
  next_order,
  toogleIsCurrent,
}) => {
  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <DriverSection vehicle={vehicle} onCall={onCall} />
      <VehicleSection vehicle={vehicle} />
      {!!order && (
        <OrderSection order={order} />
      )}
      {!!next_order && (
        <Button
          variant="contained"
          size="small"
          startIcon={!isCurrent && <Icon >west</Icon>}
          endIcon={isCurrent && <Icon >east</Icon>}
          onClick={toogleIsCurrent}
        >
          {isCurrent ? I18n.t('supervision.vehicle.card.next_order') : I18n.t('supervision.vehicle.card.back')}
        </Button>
      )}
    </Box>
  )
}

export default React.memo(VehicleCard)
